import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Globe, ArrowRight, ExternalLink } from "lucide-react";

// ── Import your logo from assets ──────────────────────────────────
// Replace the path below with your actual logo file path

// Example alternatives:
// import RevaLogo from "../assets/reva-logo.png";
import RevaLogo from "./Images/1111.png";

// ── Data ──────────────────────────────────────────────────────────
const QUICK_LINKS = [
  { label: "Home",             path: "/"                },
  { label: "About Conference", path: "/conference"      },
  { label: "Research Areas",   path: "/research-areas"  },
  { label: "Paper Submission", path: "/paper-submission"},
  { label: "Committee",        path: "/committee"       },
  { label: "Speakers",         path: "/speakers"        },
  { label: "Registration",     path: "/registration"    },
  { label: "Contact",          path: "/contact"         },
];

const IMPORTANT_DATES = [
  { label: "Abstract Submission",       date: "June 15, 2026"  },
  { label: "Abstract Acceptance",       date: "June 30, 2026"  },
  { label: "Full Paper Submission",     date: "July 15, 2026"  },
  { label: "Full Paper Acceptance",     date: "July 20, 2026"  },
  { label: "Last Date of Registration", date: "July 25, 2026"  },
  { label: "Conference",                date: "Jul 31–Aug 1, 2026" },
];

const SDG_GOALS = [
  { num: "6",  color: "#26BDE2", label: "Clean Water"          },
  { num: "9",  color: "#F36D25", label: "Infrastructure"        },
  { num: "11", color: "#F99D26", label: "Sustainable Cities"    },
  { num: "12", color: "#BF8B2E", label: "Responsible Cons."     },
];

// ─────────────────────────────────────────────────────────────────
export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="w-full bg-[#0d1117] text-white">

      {/* ── Top CTA Strip ── */}
      <div className="border-b border-white/8 bg-[#111827]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12 py-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <div>
            <p className="text-white text-[18px] font-black leading-tight">
              Submit Your Paper to SMPACE-2026
            </p>
            <p className="text-white/50 text-[13px] mt-1">
              Abstract deadline:&nbsp;
              <span className="text-orange-400 font-semibold">June 15, 2026</span>
              &nbsp;·&nbsp; SCOPUS Indexed Springer Proceedings
            </p>
          </div>
          <div className="flex flex-wrap gap-3 flex-shrink-0">
            <Link
              to="/paper-submission"
              className="inline-flex items-center gap-2 bg-[#F58220] hover:bg-[#d96d12] text-white text-[13px] font-bold px-5 py-2.5 rounded-lg transition-all duration-200 group"
            >
              Submit Paper
              <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <Link
              to="/registration"
              className="inline-flex items-center gap-2 border border-white/20 hover:border-[#F58220] text-white hover:text-[#F58220] text-[13px] font-semibold px-5 py-2.5 rounded-lg transition-all duration-200"
            >
              Register Now
            </Link>
          </div>
        </div>
      </div>

      {/* ── Main Footer Grid ── */}
      <div className="max-w-screen-xl mx-auto px-6 lg:px-12 py-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">

          {/* ── Col 1 — Brand ── */}
          <div className="lg:col-span-1">
            {/* Logo + name */}
            <Link to="/" className="flex items-center gap-3 mb-5 group w-fit">
              <img
                src={RevaLogo}
                alt="REVA University"
                className="h-[150px] w-[150px] object-contain drop-shadow-sm"
                onError={(e) => {
                  // Fallback if image doesn't load
                  e.target.style.display = "none";
                }}
              />
              
            </Link>

            {/* SMPACE badge */}
            <div className="inline-flex flex-col mb-5">
              <span className="bg-[#F58220] text-white text-[10px] font-black tracking-[0.16em] uppercase px-3 py-1.5 rounded-sm w-fit">
                SMPACE-2026
              </span>
              <span className="text-white/50 text-[12px] mt-2 leading-relaxed">
                10th International Conference on Sustainable Materials &amp; Practices for
                Advancements in Civil Engineering
              </span>
            </div>

            {/* SDG chips */}
            <div>
              <p className="text-white/30 text-[10px] font-bold tracking-widest uppercase mb-2.5">
                Aligned with UN SDGs
              </p>
              <div className="flex items-center gap-2">
                {SDG_GOALS.map((s) => (
                  <div
                    key={s.num}
                    title={s.label}
                    className="w-9 h-9 rounded-lg flex flex-col items-center justify-center cursor-default"
                    style={{ backgroundColor: s.color }}
                  >
                    <span className="text-white text-[8px] font-bold opacity-80 leading-none">SDG</span>
                    <span className="text-white text-[14px] font-black leading-none">{s.num}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Col 2 — Quick Links ── */}
          <div>
            <p className="text-white text-[13px] font-black uppercase tracking-[0.14em] mb-5">
              Quick Links
            </p>
            <ul className="space-y-2.5">
              {QUICK_LINKS.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className="flex items-center gap-2 text-white/55 hover:text-[#F58220] text-[13px] font-medium transition-colors duration-150 group"
                  >
                    <span className="w-1 h-1 rounded-full bg-[#F58220]/40 group-hover:bg-[#F58220] flex-shrink-0 transition-colors" />
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* ── Col 3 — Important Dates ── */}
          <div>
            <p className="text-white text-[13px] font-black uppercase tracking-[0.14em] mb-5">
              Important Dates
            </p>
            <ul className="space-y-3">
              {IMPORTANT_DATES.map((d, i) => (
                <li key={i} className="flex items-start justify-between gap-3">
                  <span className="text-white/50 text-[12px] leading-snug">{d.label}</span>
                  <span className="text-white text-[12px] font-bold whitespace-nowrap flex-shrink-0">
                    {d.date}
                  </span>
                </li>
              ))}
            </ul>

            {/* Publication partner */}
            <div className="mt-7 pt-6 border-t border-white/8">
              <p className="text-white/30 text-[10px] font-bold tracking-widest uppercase mb-3">
                Publication Partner
              </p>
              <div className="inline-flex items-center gap-2 bg-white/6 border border-white/10 rounded-xl px-4 py-3">
                <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
                  <span className="text-gray-800 text-[11px] font-black">Sp</span>
                </div>
                <div>
                  <p className="text-white text-[14px] font-black leading-tight">Springer</p>
                  <p className="text-white/40 text-[11px]">SCOPUS Indexed</p>
                </div>
              </div>
            </div>
          </div>

          {/* ── Col 4 — Contact ── */}
          <div>
            <p className="text-white text-[13px] font-black uppercase tracking-[0.14em] mb-5">
              Contact Us
            </p>

            <ul className="space-y-4">
              {/* Address */}
              <li className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg bg-[#F58220]/12 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <MapPin size={13} className="text-[#F58220]" />
                </div>
                <div>
                  <p className="text-white/40 text-[10px] font-bold tracking-widest uppercase mb-0.5">Address</p>
                  <p className="text-white/65 text-[12px] leading-relaxed">
                    Rukmini Knowledge Park,<br />
                    Kattigenahalli, Yelahanka,<br />
                    Bengaluru – 560064
                  </p>
                </div>
              </li>

              {/* Phone */}
              <li className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg bg-[#F58220]/12 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Phone size={13} className="text-[#F58220]" />
                </div>
                <div>
                  <p className="text-white/40 text-[10px] font-bold tracking-widest uppercase mb-0.5">Phone</p>
                  <a
                    href="tel:+918046966966"
                    className="text-white/65 hover:text-[#F58220] text-[13px] font-medium transition-colors"
                  >
                    +91-80-4696 6966
                  </a>
                </div>
              </li>

              {/* Email */}
              <li className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg bg-[#F58220]/12 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Mail size={13} className="text-[#F58220]" />
                </div>
                <div>
                  <p className="text-white/40 text-[10px] font-bold tracking-widest uppercase mb-0.5">Email</p>
                  <a
                    href="mailto:ruceconference@reva.edu.in"
                    className="text-white/65 hover:text-[#F58220] text-[12px] font-medium transition-colors break-all"
                  >
                    ruceconference@reva.edu.in
                  </a>
                </div>
              </li>

              {/* Website */}
              <li className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg bg-[#F58220]/12 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Globe size={13} className="text-[#F58220]" />
                </div>
                <div>
                  <p className="text-white/40 text-[10px] font-bold tracking-widest uppercase mb-0.5">Website</p>
                  <a
                    href="https://www.reva.edu.in"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/65 hover:text-[#F58220] text-[13px] font-medium transition-colors flex items-center gap-1"
                  >
                    www.reva.edu.in
                    <ExternalLink size={11} className="opacity-50" />
                  </a>
                </div>
              </li>
            </ul>

            {/* Organised by */}
            <div className="mt-6 pt-5 border-t border-white/8">
              <p className="text-white/30 text-[10px] font-bold tracking-widest uppercase mb-2">
                Organised by
              </p>
              <p className="text-white/70 text-[12px] font-semibold leading-snug">
                School of Civil Engineering
              </p>
              <p className="text-white/40 text-[11px] mt-0.5">
                Rukmini Educational Charitable Trust
              </p>
            </div>
          </div>

        </div>
      </div>

      {/* ── Bottom Bar ── */}
      <div className="border-t border-white/8">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-white/35 text-[12px]">
            © {year} REVA University · SMPACE-2026 · All rights reserved.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-white/25 text-[11px]">
              Rukmini Educational Charitable Trust
            </span>
            <span className="w-px h-3 bg-white/15" />
            <a
              href="https://www.reva.edu.in"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/35 hover:text-[#F58220] text-[12px] transition-colors"
            >
              www.reva.edu.in
            </a>
          </div>
        </div>
      </div>

      {/* ── Bottom orange accent line ── */}
      <div className="h-[3px] bg-gradient-to-r from-[#F58220] via-orange-300 to-[#F58220]" />

    </footer>
  );
}